import { Suspense, useEffect, useRef, useState, type ReactNode } from "react";

interface DeferredSectionProps {
  children: ReactNode;
  minHeight?: string;
  rootMargin?: string;
}

/**
 * Viewport-gated mount wrapper. Reserves vertical space to prevent CLS and
 * only renders children (and triggers their lazy chunk fetch) when the
 * placeholder is within `rootMargin` of the viewport.
 */
const DeferredSection = ({
  children,
  minHeight = "400px",
  rootMargin = "400px 0px",
}: DeferredSectionProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (mounted) return;
    if (typeof IntersectionObserver === "undefined") {
      setMounted(true);
      return;
    }
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setMounted(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [mounted, rootMargin]);

  return (
    <div ref={ref} style={mounted ? undefined : { minHeight }}>
      {mounted ? <Suspense fallback={null}>{children}</Suspense> : null}
    </div>
  );
};

export default DeferredSection;